/**
 * Hook: useAdminTenantManagement
 * Descrição: Hook para Root Admin visualizar dados de qualquer tenant
 * 
 * Diferente do useTenantManagement, este hook:
 * - Recebe tenantId como parâmetro (não usa contexto)
 * - Busca dados como admin (sem RLS de tenant)
 * - Não tem ações de billing (admin apenas visualiza)
 * 
 * @author Sistema
 * @update 2026-01-19 - Criação para tela Admin Tenant Management
 */

import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useTenantBillingAdmin } from "@/hooks/useTenantBillingAdmin";
import { useTenantDashboardStatsAdmin, useTenantDashboardTimeSeriesAdmin } from "@/hooks/useTenantDashboardStatsAdmin";
import { useResourceLimitsAdmin } from "@/hooks/useResourceLimitsAdmin";
import { TenantManagementData, TenantMemberWithProfile, TenantInboxSummary } from "@/hooks/useTenantManagement";
import { logger } from "@/lib/logger";

// ============================================================================
// HOOK PRINCIPAL
// ============================================================================

export function useAdminTenantManagement(tenantId: string | null) {
  // Hooks adaptados para modo admin
  const billingHook = useTenantBillingAdmin(tenantId);
  const statsHook = useTenantDashboardStatsAdmin(tenantId ?? undefined);
  const timeSeriesHook = useTenantDashboardTimeSeriesAdmin(tenantId ?? undefined, 7);
  const resourceLimitsHook = useResourceLimitsAdmin(tenantId);

  // Query para buscar dados básicos do tenant
  const { data: tenantData, isLoading: tenantLoading } = useQuery({
    queryKey: ["admin-tenant-data", tenantId],
    queryFn: async () => {
      if (!tenantId) return null;

      logger.debug("useAdminTenantManagement", "Buscando dados do tenant", { tenantId });

      const { data, error } = await supabase
        .from("tenants")
        .select("id, name, slug, status, created_at")
        .eq("id", tenantId)
        .single();

      if (error) {
        logger.error("useAdminTenantManagement", "Erro ao buscar tenant", { error });
        throw error;
      }

      return data;
    },
    enabled: !!tenantId,
    staleTime: 2 * 60 * 1000,
  });

  // Query para buscar membros do tenant via RPC
  const { data: members, isLoading: membersLoading } = useQuery({
    queryKey: ["admin-tenant-members", tenantId],
    queryFn: async () => {
      if (!tenantId) return [];

      logger.debug("useAdminTenantManagement", "Buscando membros via RPC", { tenantId });

      // Usar RPC get_tenant_members_for_audit com campos completos
      const { data, error } = await supabase.rpc("get_tenant_members_for_audit", {
        p_tenant_id: tenantId,
      });

      if (error) {
        logger.error("useAdminTenantManagement", "Erro ao buscar membros", { error });
        throw error;
      }

      // Transformar retorno da RPC para formato TenantMemberWithProfile
      return (data || []).map((row: any) => ({
        id: row.member_id,
        user_id: row.user_id,
        tenant_id: row.tenant_id,
        is_owner: row.is_owner,
        is_active: row.is_active,
        online_status: row.online_status,
        last_seen_at: row.last_seen_at,
        created_at: row.joined_at, // Mapear joined_at para created_at (compatibilidade UI)
        profile: {
          id: row.profile_id || row.user_id,
          full_name: row.full_name,
          email: row.email,
          avatar_url: row.avatar_url,
        },
        permission_profile: {
          id: row.permission_profile_id || row.user_id,
          name: row.permission_profile_name || row.role,
          slug: row.permission_profile_slug,
        },
      })) as TenantMemberWithProfile[];
    },
    enabled: !!tenantId,
    staleTime: 2 * 60 * 1000,
  });

  // Query para buscar inboxes do tenant
  const { data: inboxes, isLoading: inboxesLoading } = useQuery({
    queryKey: ["admin-tenant-inboxes", tenantId],
    queryFn: async () => {
      if (!tenantId) return [];

      logger.debug("useAdminTenantManagement", "Buscando inboxes do tenant", { tenantId });

      const { data, error } = await supabase
        .from("inboxes")
        .select(`
          id,
          name,
          status,
          channel_type_id,
          provider_id,
          created_at,
          channel_type:channel_types(
            display_name,
            icon
          ),
          provider:api_providers(
            display_name
          )
        `)
        .eq("tenant_id", tenantId)
        .order("status", { ascending: true })
        .order("created_at", { ascending: false });

      if (error) {
        logger.error("useAdminTenantManagement", "Erro ao buscar inboxes", { error });
        throw error;
      }

      return (data || []).map((inbox: any) => ({
        id: inbox.id,
        name: inbox.name,
        status: inbox.status,
        channel_type_id: inbox.channel_type_id,
        provider_id: inbox.provider_id,
        provider_display_name: inbox.provider?.display_name || null,
        created_at: inbox.created_at,
        channel_type: inbox.channel_type ? {
          display_name: inbox.channel_type.display_name,
          icon: inbox.channel_type.icon,
        } : undefined,
      })) as TenantInboxSummary[];
    },
    enabled: !!tenantId,
    staleTime: 2 * 60 * 1000,
  });

  // Consolidar dados
  const consolidatedData: TenantManagementData | null = tenantId && tenantData ? {
    tenant: {
      id: tenantData.id,
      name: tenantData.name,
      slug: tenantData.slug,
      status: tenantData.status || "active",
      created_at: tenantData.created_at || "",
    },

    billing: {
      plan: billingHook.currentPlan,
      planName: billingHook.plans?.find(p => p.id === billingHook.currentPlan)?.name || billingHook.currentPlan,
      billingPeriod: billingHook.billing?.billing_period || "monthly",
      subscriptionStatus: billingHook.billing?.subscription_status || "inactive",
      currentPeriodEnd: billingHook.billing?.current_period_end || null,
      currentPeriodStart: billingHook.billing?.current_period_start || null,
      trialEndsAt: billingHook.billing?.trial_ends_at || null,
      isInTrial: billingHook.isInTrial,
      trialDaysRemaining: billingHook.trialDaysRemaining,
      hasActiveSubscription: billingHook.hasActiveSubscription,
    },

    messages: {
      sent: statsHook.data?.messages.sent || 0,
      received: statsHook.data?.messages.received || 0,
      total: statsHook.data?.messages.total || 0,
    },

    conversations: {
      pending: statsHook.data?.conversations.pending || 0,
      open: statsHook.data?.conversations.open || 0,
      closed: statsHook.data?.conversations.closed || 0,
      total: statsHook.data?.conversations.total || 0,
    },

    limits: {
      maxAgents: resourceLimitsHook.limits?.effective_max_agents || 0,
      maxInboxes: resourceLimitsHook.limits?.effective_max_inboxes || 0,
      maxMessages: resourceLimitsHook.limits?.effective_max_messages || 0,
      isEnterprise: resourceLimitsHook.isEnterprise,
      hasCampaignsModule: resourceLimitsHook.hasCampaignsModule,
    },

    usage: {
      currentAgents: members?.length || 0,
      currentInboxes: inboxes?.filter(i => i.status !== "suspended").length || 0,
    },

    members: members || [],
    inboxes: inboxes || [],

    activeAddons: resourceLimitsHook.tenantAddons?.map(addon => ({
      id: addon.id,
      name: addon.addon_name || "Add-on",
      quantity: addon.quantity,
      status: addon.status,
    })) || [],
  } : null;

  // Estado de loading consolidado
  const isLoading = 
    tenantLoading ||
    billingHook.isLoading ||
    statsHook.isLoading ||
    resourceLimitsHook.isLoading ||
    membersLoading ||
    inboxesLoading;

  return {
    data: consolidatedData,
    billing: billingHook,
    stats: statsHook,
    timeSeries: timeSeriesHook,
    resourceLimits: resourceLimitsHook,
    isLoading,
    tenantId,
    // Admin não tem openCustomerPortal, apenas sync
    syncSubscription: billingHook.syncSubscription,
    isSyncingSubscription: billingHook.isSyncingSubscription,
  };
}
